import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Mail, MapPin, Phone } from "lucide-react";

export default function Contact() {
    const [searchParams] = useSearchParams();
    const requestedService = searchParams.get("service") || "";
    const [form, setForm] = useState({ name: "", email: "", subject: requestedService ? `Event enquiry: ${requestedService}` : "", message: "" });
    const [eventDetails, setEventDetails] = useState({ date: "", location: "", guests: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.subject || !form.message) return toast.error("Please fill all required fields");
        setLoading(true);
        try {
            const details = [
                eventDetails.date && `Preferred date: ${eventDetails.date}`,
                eventDetails.location && `Event location: ${eventDetails.location}`,
                eventDetails.guests && `Estimated guests: ${eventDetails.guests}`,
                form.message,
            ].filter(Boolean).join("\n\n");
            await api.post("/support/tickets", { ...form, message: details });
            toast.success("Enquiry sent — our event team will be in touch");
            setForm({ name: "", email: "", subject: "", message: "" });
            setEventDetails({ date: "", location: "", guests: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send message");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 pb-24" data-testid="contact-page">
            <SEO title="Plan an Event" description="Tell X-Plosiv about your event, preferred date and location. Our team will help shape the next steps." />
            <section className="pt-4">
                <p className="font-mono-ui text-muted-foreground">Tell us what you are imagining</p>
                <h1 className="dw-headline text-5xl sm:text-6xl lg:text-7xl mt-4">Let's plan a moment.</h1>
                <p className="mt-6 text-muted-foreground max-w-xl">Share a few details about the occasion. Our event team will get back to discuss a plan shaped around you.</p>
            </section>

            <section className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-16">
                <div className="space-y-6">
                    {[
                        { icon: Mail, label: "Email", value: "nitin.xplosiv@gmail.com", href: "mailto:nitin.xplosiv@gmail.com" },
                        { icon: Phone, label: "Call or WhatsApp", value: "+91 98105 25439", href: "https://wa.me/919810525439" },
                        { icon: MapPin, label: "Based in", value: "Jasola, New Delhi · India" },
                    ].map((c, i) => (
                        <a key={i} href={c.href} className="flex items-start gap-4 group">
                            <div className="h-11 w-11 rounded-full bg-card border border-border flex items-center justify-center flex-shrink-0 group-hover:border-[hsl(var(--accent))] group-hover:text-[hsl(var(--accent))] transition-colors">
                                <c.icon className="h-4 w-4" />
                            </div>
                            <div>
                                <p className="font-mono-ui text-muted-foreground">{c.label}</p>
                                <p className="font-display text-2xl mt-1 group-hover:text-[hsl(var(--accent))] transition-colors">{c.value}</p>
                            </div>
                        </a>
                    ))}
                </div>

                <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4">
                    <p className="font-display text-2xl">Your event brief</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="rounded-full bg-background/60 border border-border px-5 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" data-testid="contact-name-input" />
                        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" className="rounded-full bg-background/60 border border-border px-5 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" data-testid="contact-email-input" />
                    </div>
                    <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What are you planning?" className="w-full rounded-full bg-background/60 border border-border px-5 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" data-testid="contact-subject-input" />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <input type="date" value={eventDetails.date} onChange={(e) => setEventDetails({ ...eventDetails, date: e.target.value })} aria-label="Preferred event date" className="min-w-0 rounded-full bg-background/60 border border-border px-4 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" />
                        <input value={eventDetails.location} onChange={(e) => setEventDetails({ ...eventDetails, location: e.target.value })} placeholder="Event location" className="min-w-0 rounded-full bg-background/60 border border-border px-4 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" />
                        <input inputMode="numeric" value={eventDetails.guests} onChange={(e) => setEventDetails({ ...eventDetails, guests: e.target.value })} placeholder="Approx. guests" className="min-w-0 rounded-full bg-background/60 border border-border px-4 py-3 text-sm outline-none focus:border-[hsl(var(--accent))]" />
                    </div>
                    <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about the occasion, your ideas, and what would make it a success." className="w-full rounded-2xl bg-background/60 border border-border px-5 py-3 text-sm outline-none focus:border-[hsl(var(--accent))] min-h-[160px]" data-testid="contact-message-input" />
                    <button type="submit" disabled={loading} className="w-full dw-btn-primary h-12 font-mono-ui text-xs" data-testid="contact-submit-button">
                        {loading ? "Sending…" : "Send event enquiry"}
                    </button>
                </form>
            </section>
        </div>
    );
}
